import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import {
  BentoCategory,
  OrganizationNiche,
  OrganizationNicheStatus,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import PickerDropdown from "features/Auth/SelfServeVaOnboarding/NichePick/Picker";
import { fetcherAuth } from "utils/api";

interface Props {
  open: boolean;
  handleClose: () => void;
  categoriesToPickFrom: BentoCategory[];
  onNicheCreated: (newNiche: OrganizationNiche) => void;
}

const styles = {
  textField: {
    backgroundColor: "white",
    "& .MuiInputBase-root": { backgroundColor: "white" },
    "& .MuiFormHelperText-root": { color: "error.main" },
  },
};

export default function CreateNicheDialog({
  open,
  handleClose,
  categoriesToPickFrom,
  onNicheCreated,
}: Props) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);

  const [formData, setFormData] = useState<OrganizationNiche>({
    name: "",
    notes: "",
    status: OrganizationNicheStatus.pending,
  });

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (name === "name") setNameError(false);
  };

  const onChangeNicheCategory = (e: CustomEvent) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      bentoCategoryId: value,
      name:
        categoriesToPickFrom.find((category) => category.id === value)?.name ||
        prev.name,
    }));
  };

  const handleCreate = async () => {
    // Validation
    let hasErrors = false;

    if (!formData.name.trim()) {
      setNameError(true);
      hasErrors = true;
    }

    if (hasErrors) {
      setAlert("Please fill in all required fields", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches`,
        "POST",
        {},
        {
          name: formData.name,
          notes: formData.notes,
          bentoCategoryId: formData.bentoCategoryId,
        },
      );

      onNicheCreated(res.niche);
      setAlert("Successfully created niche", "success");

      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setNameError(false);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen={isMobileScreen}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Create New Niche</DialogTitle>
      <DialogContent>
        <Grid container gap={2}>
          <Grid item xs={12}>
            <FormHelperText>Niche Category</FormHelperText>
            <PickerDropdown
              categoriesToPickFrom={categoriesToPickFrom}
              niche={formData}
              onChange={onChangeNicheCategory}
            />
          </Grid>

          <Grid item xs={12}>
            <FormHelperText>Niche Name *</FormHelperText>
            <TextField
              sx={styles.textField}
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={nameError}
              placeholder="Enter niche name"
            />
          </Grid>

          <Grid item xs={12}>
            <FormHelperText>
              Notes - Include <strong>ALL details</strong> we will need to pick
              fitting brands for you
            </FormHelperText>
            <TextField
              sx={styles.textField}
              fullWidth
              multiline
              rows={4}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="For example, how old your kids are; what kind of pet you have; or where in the world you would like to travel"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleDialogClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={handleCreate}
          variant="contained"
        >
          Create Niche
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
